import { oc } from '@orpc/contract';
import { ROUTED_TO, TICKET_STATUSES, WAITING_ON } from '@lsc/domain';
import { z } from 'zod';
import { ticketHolder } from './presence.js';
import { ticketDetail, ticketGet } from './ticket-detail.js';
import { ticketClaimInput, ticketCloseInput, ticketCommentInput, ticketCreate, ticketReopenInput, ticketRouteInput, ticketSetStatusInput, ticketMoveStageInput, ticketNoteDeleteInput, ticketNoteEditInput } from './ticket-write.js';
const action = (path, summary) => oc.route({ method: 'POST', path: `/tickets/${path}`, summary });
export const boardTicket = z.object({
    ticketId: z.uuid(),
    ticketNumber: z.string(),
    subject: z.string(),
    status: z.enum(TICKET_STATUSES),
    routedTo: z.enum(ROUTED_TO).nullable(),
    waitingOn: z.enum(WAITING_ON).nullable(),
    staffCategory: z.string().nullable(),
    product: z.string().nullable(),
    source: z.string(),
    accountId: z.uuid().nullable(),
    accountLabel: z.string().nullable(),
    /** What the rep typed when no account is linked. Shown with an unconfirmed marker. */
    callerCompany: z.string().nullable(),
    callerName: z.string().nullable(),
    assigneeName: z.string().nullable(),
    /** Who holds it, for coverage against who is on duty. */
    holders: z.array(ticketHolder),
    /** Slack user id of whoever logged it, until every rep has a profile. */
    legacyCreatedBy: z.string().nullable(),
    /** Who logged it, as a name: the profile's, else the Slack directory's for a mirrored card. */
    loggedByName: z.string().nullable(),
    /** The Slack list's Stage and Status for a mirrored card (the board's columns follow the list until the switch). */
    slackStage: z.string().nullable(),
    /** `slack:Rec…` for a list ticket, and the #support-tickets thread for a desk one: the card's link into Slack. */
    legacyRef: z.string().nullable(),
    slackThreadTs: z.string().nullable(),
    /** YYYY-MM-DD, while the stage is Scheduled. */
    scheduledFor: z.string().nullable(),
    slackStatus: z.string().nullable(),
    createdAt: z.string(),
    lastActivityAt: z.string().nullable(),
    closedAt: z.string().nullable(),
});
export const boardOutput = z.object({
    /** Active work: open, in progress, waiting, stuck. */
    active: z.array(boardTicket),
    /** Resolved or closed since the start of today (UTC). */
    doneToday: z.array(boardTicket),
    totalActive: z.number().int(),
    truncated: z.boolean(),
    /** When the mirror last finished a pass, so the board can say how fresh it is. */
    mirroredAt: z.string().nullable(),
});
export const ticketsContract = {
    get: ticketGet,
    create: ticketCreate,
    claim: action('claim', 'Take the ticket').input(ticketClaimInput).output(ticketDetail),
    route: action('route', 'Set who owns it now and what it waits on').input(ticketRouteInput).output(ticketDetail),
    setStatus: action('set-status', 'Move between live statuses').input(ticketSetStatusInput).output(ticketDetail),
    resolve: action('resolve', 'Resolve with a resolution and reason').input(ticketCloseInput).output(ticketDetail),
    close: action('close', 'Close with a resolution and reason').input(ticketCloseInput).output(ticketDetail),
    reopen: action('reopen', 'Reopen a resolved or closed ticket').input(ticketReopenInput).output(ticketDetail),
    comment: action('comment', 'Add an internal note').input(ticketCommentInput).output(ticketDetail),
    editNote: action('note/edit', 'Change your own note').input(ticketNoteEditInput).output(ticketDetail),
    deleteNote: action('note/delete', 'Remove your own note').input(ticketNoteDeleteInput).output(ticketDetail),
    refreshSummary: action('summary/refresh', 'Ask for the AI summary to be brought up to date (the gate decides)').input(z.object({ ticketId: z.uuid() })).output(z.object({ queued: z.boolean(), message: z.string().nullable() })),
    moveStage: action('stage', 'Move to another board stage (written back to the Slack list)').input(ticketMoveStageInput).output(ticketDetail),
    board: oc.route({ method: 'POST', path: '/tickets/board', summary: 'Active tickets for the board plus today\'s done strip' }).input(z.object({}).optional()).output(boardOutput),
};
//# sourceMappingURL=tickets.js.map